<template lang="pug">
.content.flex.justify-end.h-full
  .bar-left(:class="[ 'w-1/3' ]" v-if="cellSelected")
    .builder(v-if="displayBuilder")
      h2.mt-8.mb-2 Builder
      .btn-build(v-for="building in availableBuildings" @click="performBuild(building.name, building.level)")
        Building(:cell="building")

    BuildingInfo(
      v-if="buildingSelected"
      :building="buildingSelected"
      :upgrade="getPossibleUpgrade(cellSelected)"
      :canAffordUpgrade="canConstruct(getPossibleUpgrade(cellSelected))"
      :inventory="inventory"
      @upgrade="upgradeBuilding(cellSelected)"
      @enable="enableBuilding(cellSelected)"
      @disable="disableBuilding(cellSelected)"
      @enable-all="enableBuildingAll(cellSelected)"
      @disable-all="disableBuildingAll(cellSelected)"
      @destroy="removeBuilding(cellSelected)"

      @move="startMoveBuilding(cellSelected)"
    )

  .board-container.w-full.h-screen.flex(@click="cellSelected = null")
    .inventory
      .item.whitespace-no-wrap.ml-4.mt-2.flex(v-for="(val, id) in inventory")
        Icon(size="sm" :item="id")
        span.value.text-black {{ Math.floor(val) }}
        span.value.text-gray-500 /{{ Math.ceil(storage[id] + val) }}

    .board(v-if="board")
      .cell(:class="getPositionCellFrom(id)" v-for="id in board.hull")
        .btn-select(v-if="findBuilding(id)" @click.stop="selectBuilding(id)")
          Building(:cell="findBuilding(id)")
        .empty(v-if="! findBuilding(id)")
          a.btn-void.btn-add(@click.stop="selectBuilding(id)") +

  .bar-right.flex.flex-col.justify-start(:class="[ 'w-1/3' ]")
    Laboratory(
      :researches="availableResearches"
      :inventory="inventory"
      @research="researchTechnology"
    )

    .section.section-debug(v-if="god")
      h2.mt-8.mb-2.cursor-pointer(@click="debugIsVisible = !debugIsVisible") Debug Commands
      .debug-content(v-if="debugIsVisible")
        .btn-block(@click="update()") Tick
        .btn-block(@click="isPlaying = true" v-if="! isPlaying") Play
        .btn-block(@click="isPlaying = false" v-if="isPlaying") Pause
</template>

<script>

import { BuildingFactory, BoardManager } from '@/engine/BoardManagerAdapter.js'

import Building from '@/components/Building'
import BuildingInfo from '@/components/BuildingInfo'
import Laboratory from '@/components/Laboratory'

import GameApi from '@/plugins/GameApi'

const now = () => Math.round((new Date()).getTime() / 1000)

const factory = new BuildingFactory()
const boardManager = new BoardManager()

if (document) {
  document.boardManager = boardManager
}

export default {
  name: 'Board',
  components: { Building, BuildingInfo, Laboratory },
  data: () => ({
    boardId: null,
    hasLoaded: false,
    lastSavedTimestamp: 0,
    board: null,
    cellSelected: null,
    isPlaying: true,

    availableBuildings: [],
    availableResearches: [],

    movingBuilding: null,

    god: false,
    debugIsVisible: false,

    serializedDatabase: null,
    timeTick: null,
    tickEvery: 100
  }),
  computed: {
    displayBuilder () {
      return this.cellSelected && !this.buildingSelected
    },
    buildingSelected () {
      if (this.cellSelected) {
        return this.findBuilding(this.cellSelected)
      }
      return null
    },

    btnClasses () {
      return [
        'inline-flex',
        'items-center',
        'justify-center',
        'px-5',
        'py-3',
        'border',
        'border-transparent',
        'text-base',
        'leading-6',
        'font-medium',
        'rounded-md',
        'text-white',
        'bg-indigo-600',
        'hover:bg-indigo-500',
        'focus:outline-none',
        'focus:shadow-outline',
        'transition',
        'duration-150',
        'ease-in-out'
      ]
    },
    inventory () {
      return this.board ? this.board.inventory : {}
    },
    storage () {
      return this.board ? this.board.storage : {}
    }
  },
  watch: {
    board (value) {
      let toStore = JSON.stringify(value)
      if (this.serializedDatabase !== toStore) {
        if (this.hasToSave()) { // 15 secs
          this.saveBoard()
        }
        this.serializedDatabase = toStore
      }
    }
  },
  async mounted () {
    this.boardId = this.$route.params.id

    await this.loadBoard()

    if (this.timeTick) {
      clearTimeout(this.timeTick)
      this.timeTick = null
    }

    this.timeTick = setInterval(() => {
      if (this.isPlaying) {
        this.update()
      }
    }, this.tickEvery)

    window.addEventListener('keyup', this.onKeyUp.bind(this))
    window.addEventListener('keydown', this.onKeyDown.bind(this))
  },
  beforeDestroy () {
    window.removeEventListener('keyup', this.onKeyUp.bind(this))
    window.removeEventListener('keydown', this.onKeyDown.bind(this))

    if (this.timeTick) {
      clearTimeout(this.timeTick)
      this.timeTick = null
    }
  },
  methods: {

    hasToSave () {
      return now() > this.lastSavedTimestamp + 15 // every 15 secs
    },
    saveBoard () {
      if (this.hasLoaded) { // IMPORTANT! Avoid saving a empty board (can occur race condition if not)
        console.log('Saving board...')
        let data = JSON.stringify(boardManager.export())
        GameApi.saveBoard(this.boardId, data)
        this.lastSavedTimestamp = now()
      }
    },

    startMoveBuilding (fromId) {
      this.movingBuilding = fromId
      this.cellSelected = null
    },
    performMoveBuilding (toId) {
      let fromId = this.movingBuilding
      this.movingBuilding = null

      boardManager.moveBuildings(fromId, toId)
      this.saveBoard()
    },

    onKeyUp (event) {
      if (event.key === 'Alt') {
        this.disableGodMode()
      }
    },
    onKeyDown (event) {
      if (event.key === 'Alt' && this.$route.query.mode === 'god') {
        this.enableGodMode()
      }
    },

    enableGodMode () {
      this.god = true
    },
    disableGodMode () {
      this.god = false
    },

    async loadBoard () {

      let board = await GameApi.loadBoard(this.boardId)

      if (!board) {
        throw new Error('Unable to find board')
      }

      if (!board) {
        // create new board!
        this.hasLoaded = true
        this.board = boardManager.export()
        this.refreshBuildingsAndResearches()
        this.saveBoard()

        return
      }

      let { data } = board

      if (!data) {
        throw new Error(`Unable to load data from server: ${data}`)
      }

      boardManager.load(JSON.parse(data))
      this.board = boardManager.export()
      this.refreshBuildingsAndResearches()

      this.hasLoaded = true
    },

    refreshBuildingsAndResearches () {
      this.availableBuildings = boardManager.availableBuildings()
        .filter(x => x.config.level === 0)
        .filter(x => !x.config.requiredTechnology || boardManager.researched(x.config.requiredTechnology))

      this.availableResearches = boardManager.availableResearches()
        .filter(x => !x.requiredTechnology || boardManager.researched(x.requiredTechnology))
    },

    researchTechnology (id) {
      if (this.god) {
        boardManager.forceResearch(id)
        this.refreshBuildingsAndResearches()
        return
      }

      boardManager.research(id)
      this.saveBoard()

      this.refreshBuildingsAndResearches()
    },

    canConstruct (building) {
      if (!building) {
        return null
      }
      return boardManager.canConstruct(building.config.name, building.config.level)
    },

    canResearch (id) {
      return boardManager.canResearch(id)
    },

    selectBuilding (id) {
      if (this.movingBuilding) {
        this.performMoveBuilding(id)
      } else {
        this.cellSelected = id
      }
    },
    getPossibleUpgrade (position) {
      return boardManager.upgradableByPosition(position)
    },
    upgradeBuilding (position) {
      let result
      if (this.god) {
        result = boardManager.forceUpgradeBuildingByPosition(position)
        this.saveBoard()
      }

      if (this.canConstruct(this.getPossibleUpgrade(position))) {
        result = boardManager.upgradeBuildingByPosition(position)
        this.saveBoard()
      }
      return result
    },

    enableBuilding (position) {
      boardManager.enableBuilding(position)
      this.saveBoard()
    },

    disableBuilding (position) {
      boardManager.disableBuilding(position)
      this.saveBoard()
    },

    enableBuildingAll (position) {
      let { name } = boardManager.buildings.find(x => x.position === position)
      let positions = boardManager.buildings.filter(x => x.name === name).map(x => x.position)

      for (let i of positions) {
        boardManager.enableBuilding(i)
      }
      this.saveBoard()
    },

    disableBuildingAll (position) {
      let { name } = boardManager.buildings.find(x => x.position === position)
      let positions = boardManager.buildings.filter(x => x.name === name).map(x => x.position)

      for (let i of positions) {
        boardManager.disableBuilding(i)
      }
      this.saveBoard()
    },

    removeBuilding (id) {
      boardManager.removeBuildingByPosition(id)
      this.saveBoard()
    },
    performBuild (name, level) {
      let newBuilding = factory.getBuilding(name, level)
      boardManager.addBuilding(newBuilding, this.cellSelected)
      this.cellSelected = null
      this.saveBoard()
    },
    findBuilding (id) {
      return this.board.buildings.find((x) => x.position === id)
    },
    getPositionCellFrom (id) {
      let [ x, y ] = id.split('_')
      let classes = [ `col-${y}`, `row-${x}` ]

      if (this.cellSelected === id) {
        classes.push('selected')
      }
      if (this.movingBuilding === id) {
        classes.push('movement')
      }

      return classes
    },
    update () {
      boardManager.update()
      this.board = boardManager.export()
    }
  }
}
</script>

<style lang="scss" scoped>

$grid-size: 80px;
$gutter: 4px;

.board-container {
  position: relative;
  overflow: hidden;

  .inventory {
    height: fit-content;
    z-index: 10;
  }

  .board {
    position: absolute;
    left: 50%;
    top: 50%;

    .cell {
      height: $grid-size - $gutter;
      width: $grid-size - $gutter;
      overflow: hidden;

      .empty {
        line-height: $grid-size - $gutter;
      }

      border: 3px solid rgba(150, 160, 190, .4);
      border-radius: 4px;

      &.selected {
        border: 3px solid #4bc3f1;
      }

      &.movement {
        border: 3px solid #4bc3f1;
      }

      .btn-select {
        cursor: pointer;
      }

      .btn-add {
        text-align: center;
        font-size: 24px;
        display: block;
        cursor: pointer;
      }
    }
  }
}

.building {
  text-align: center;
  > div {
    height: 74px;
    width: 74px;
  }

  .name {
    font-size: 8px;
    line-height: 8px;
    font-weight: 700;
    padding-top: 4px;
    padding-bottom: 4px;
  }

  .provides,.stores {
    .item {
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;

      .amount {
        font-size: 12px;
        line-height: 20px;
        font-weight: 700;

        &.negative {
          color: #d80c0c;
        }
      }
    }
  }
}
</style>

<style lang="scss">
body {
  margin: 0;
  padding: 0;
  font-family: 'Nova Mono', monospace;
}

$grid-size: 80px;
$gutter: 4px;

.col-0, .col-1, .col-2, .col-3, .col-4, .col-5,
.col-6, .col-7, .col-8, .col-9, .col-10, .col-11,
.row-0, .row-1, .row-2, .row-3, .row-4, .row-5,
.row-6, .row-7, .row-8, .row-9, .row-10, .row-11 {
  position: absolute;
}

.row-0  { top: $gutter / 2 + $grid-size * -4; }
.row-1  { top: $gutter / 2 + $grid-size * -3; }
.row-2  { top: $gutter / 2 + $grid-size * -2; }
.row-3  { top: $gutter / 2 + $grid-size * -1; }
.row-4  { top: $gutter / 2 + $grid-size *  0; }
.row-5  { top: $gutter / 2 + $grid-size *  1; }
.row-6  { top: $gutter / 2 + $grid-size *  2; }
.row-7  { top: $gutter / 2 + $grid-size *  3; }

.col-0  { left: $gutter / 2 + $grid-size * -6; }
.col-1  { left: $gutter / 2 + $grid-size * -5; }
.col-2  { left: $gutter / 2 + $grid-size * -4; }
.col-3  { left: $gutter / 2 + $grid-size * -3; }
.col-4  { left: $gutter / 2 + $grid-size * -2; }
.col-5  { left: $gutter / 2 + $grid-size * -1; }
.col-6  { left: $gutter / 2 + $grid-size *  0; }
.col-7  { left: $gutter / 2 + $grid-size *  1; }
.col-8  { left: $gutter / 2 + $grid-size *  2; }
.col-9  { left: $gutter / 2 + $grid-size *  3; }
.col-10 { left: $gutter / 2 + $grid-size *  4; }
.col-11 { left: $gutter / 2 + $grid-size *  5; }

.builder {
  .btn-build {
    margin: 2px;
    height: 72px;
    width: 72px;
    display: inline-block;
    overflow: hidden;
    cursor: pointer;
  }
}

.area {
  display: block;
  height: 72px;
  width: 72px;
}

.btn-block.disabled {
  background-color: #dddddd;
  cursor: not-allowed;
}

.btn-block.available {
  background-color: #f39c00;
}

.btn-block.unavailable {
  background-color: #dddddd;
  color: #888888;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/pages/starship/_id.vue